import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { CloseRounded, DownloadRounded } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { toast } from "react-toastify";
import {
  formatteDateDay,
  generateUniqueMatterName,
} from "../../../styles/styles";
import { getCurrencySymbol } from "../../../pages/LawyerPages/Settings/CurrencySetForm";
import { backend_url } from "../../../static/server";
import { useDialog } from "../../DialogContext";

const PDF_PREVIEW_WIDTH = 794;
const A4_SHEET_WIDTH = "210mm";

const isAbsoluteUrl = (value) => /^https?:\/\//i.test(value || "");

const resolveAssetUrl = (value) => {
  if (!value) return "";
  return isAbsoluteUrl(value) ? value : `${backend_url}${value}`;
};

const sanitizeFileName = (value) =>
  String(value || "Invoice")
    .replace(/[<>:"/\\|?*]+/g, "-")
    .replace(/\s+/g, "_")
    .trim();

const getDisplayName = (person = {}) => {
  const fullName =
    `${person?.first_name || ""} ${person?.last_name || ""}`.trim();
  return person?.company_name || fullName || "--";
};

const getInitials = (value) => {
  const words = String(value || "")
    .split(" ")
    .map((word) => word.trim())
    .filter(Boolean)
    .slice(0, 2);

  if (words.length === 0) return "C";
  return words.map((word) => word[0]?.toUpperCase() || "").join("");
};

const waitForFonts = async () => {
  if (document?.fonts?.ready) {
    await document.fonts.ready;
  }
};

const waitForNextPaint = () =>
  new Promise((resolve) => {
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(resolve);
    });
  });

const waitForImages = async (container) => {
  if (!container) return;

  const images = Array.from(container.querySelectorAll("img"));
  await Promise.all(
    images.map(
      (image) =>
        new Promise((resolve) => {
          if (image.complete) {
            resolve();
            return;
          }

          image.onload = () => resolve();
          image.onerror = () => resolve();
        }),
    ),
  );
};

const BillPreview = ({
  entryData,
  autoDownload = false,
  autoCloseOnDownload = false,
  layout,
  currency,
  previewMode = false,
}) => {
  const theme = useTheme();
  const { closeDialog } = useDialog();
  const billContentRef = useRef(null);
  const autoDownloadStartedRef = useRef(false);
  const { defaultCurrency, userBillLayout } = useSelector(
    (state) => state.user,
  );
  const [isDownloading, setIsDownloading] = useState(false);
  const [isPdfExportMode, setIsPdfExportMode] = useState(autoDownload);

  const resolvedLayout = layout || userBillLayout || "classic";
  const resolvedCurrency =
    currency ||
    entryData?.full_data?.currency ||
    entryData?.full_data?.case?.currency ||
    defaultCurrency ||
    "USD";
  const currencySymbol = getCurrencySymbol(resolvedCurrency) || "$";
  const compactPdfMode = isPdfExportMode;
  const extra = useMemo(() => entryData?.full_data?.extra || {}, [entryData]);
  const useExtra = Object.keys(extra).length > 0;

  const invoiceData = useMemo(() => {
    const firm = entryData?.full_data?.firm || {};
    const client = entryData?.full_data?.case?.case_related_person || {};
    const totalAmount = parseFloat(entryData?.full_data?.total_amount || 0);
    const paymentReceived = parseFloat(
      entryData?.full_data?.payment_received || 0,
    );

    return {
      firmData: {
        name:
          useExtra && extra.firm_name ? extra.firm_name : firm?.name || "--",
        address:
          useExtra && extra.firm_address
            ? extra.firm_address
            : firm?.address || "",
        city: useExtra && extra.firm_city ? extra.firm_city : firm?.city || "",
        state:
          useExtra && extra.firm_state ? extra.firm_state : firm?.state || "",
        zip:
          useExtra && extra.firm_pincode
            ? extra.firm_pincode
            : firm?.pin_code || "",
        logo: resolveAssetUrl(
          extra.firm_logo ||
            firm?.firm_logo ||
            firm?.logo ||
            firm?.logo_url ||
            "",
        ),
      },
      clientData: {
        name: getDisplayName(client),
        address:
          useExtra && extra.client_address
            ? extra.client_address
            : client?.address_1 || "",
        city:
          useExtra && extra.client_city
            ? extra.client_city
            : client?.city || "",
        state:
          useExtra && extra.client_state
            ? extra.client_state
            : client?.state || "",
        zip:
          useExtra && extra.client_pincode
            ? extra.client_pincode
            : client?.pincode || "",
        matter: entryData?.full_data?.case?.id
          ? generateUniqueMatterName(
              entryData?.full_data?.case?.id,
              entryData?.full_data?.case?.case_name,
            )
          : entryData?.full_data?.case?.case_name || "--",
        avatar: resolveAssetUrl(
          extra.client_avatar || client?.avatar || client?.avatar_url || "",
        ),
      },
      billData: {
        invoiceNumber: entryData?.invoice_num || "--",
        issueDate: formatteDateDay(entryData?.full_data?.issue_date),
        dueDate: formatteDateDay(entryData?.full_data?.due_date),
        totalAmount,
        paymentReceived,
        balanceDue: totalAmount - paymentReceived,
        status: entryData?.full_data?.status || "draft",
      },
    };
  }, [entryData, extra, useExtra]);

  const lineItems = useMemo(
    () =>
      (entryData?.full_data?.expenses || [])
        .filter((expense) => !expense?.non_billable || expense?.bill_entry_show)
        .map((expense, index) => ({
          id: expense?.id || `line-item-${index}`,
          type: expense?.type || "--",
          dueDate: formatteDateDay(entryData?.full_data?.due_date),
          quantity: Number.parseFloat(expense?.quantity || 0) || 0,
          rate:
            Number.parseFloat(expense?.hourly_rate || expense?.rate || 0) || 0,
          nonBillableAmount: expense?.non_billable
            ? Number.parseFloat(expense?.total_payment || 0) || 0
            : 0,
          billableAmount: !expense?.non_billable
            ? Number.parseFloat(expense?.total_payment || 0) || 0
            : 0,
        })),
    [entryData],
  );

  const totals = useMemo(
    () =>
      lineItems.reduce(
        (accumulator, item) => ({
          billableTotal: accumulator.billableTotal + item.billableAmount,
          nonBillableTotal:
            accumulator.nonBillableTotal + item.nonBillableAmount,
        }),
        { billableTotal: 0, nonBillableTotal: 0 },
      ),
    [lineItems],
  );

  const statusTone = useMemo(() => {
    const map = {
      draft: "info",
      pending: "warning",
      unpaid: "error",
      paid: "success",
      overdue: "warning",
    };
    return map[invoiceData.billData.status] || "default";
  }, [invoiceData.billData.status]);

  const formatCurrencyValue = useCallback(
    (value) => `${currencySymbol}${(Number.parseFloat(value) || 0).toFixed(2)}`,
    [currencySymbol],
  );

  const formatQuantityValue = useCallback((value) => {
    const numericValue = Number.parseFloat(value || 0);
    if (!Number.isFinite(numericValue)) return "0";
    return Number.isInteger(numericValue)
      ? String(numericValue)
      : numericValue.toFixed(2);
  }, []);

  // ----- Improved PDF generation with high resolution and page splitting -----
  const handleDownloadPDF = useCallback(async () => {
    if (!billContentRef.current || isDownloading) return false;

    setIsDownloading(true);
    setIsPdfExportMode(true);

    try {
      await waitForNextPaint();
      await waitForFonts();
      await waitForImages(billContentRef.current);

      const element = billContentRef.current;
      const canvas = await html2canvas(element, {
        backgroundColor: "#ffffff",
        scale: 3, // increased for crisp text
        useCORS: true,
        imageTimeout: 15000,
        logging: false,
        scrollY: -window.scrollY,
        windowWidth: PDF_PREVIEW_WIDTH,
        windowHeight: element.scrollHeight,
        onclone: (clonedDocument) => {
          const clonedBill = clonedDocument.querySelector(
            "[data-bill-pdf='true']",
          );
          if (clonedBill) {
            clonedBill.style.boxShadow = "none";
            clonedBill.style.minHeight = "297mm";
            clonedBill.style.maxWidth = `${PDF_PREVIEW_WIDTH}px`;
            clonedBill.style.width = "100%";
            clonedBill.style.borderRadius = "12px";
            clonedBill.style.padding = "24px";
          }

          clonedDocument
            .querySelectorAll("[data-pdf-hide='true']")
            .forEach((node) => {
              if (node?.style) node.style.display = "none";
            });

          clonedDocument.querySelectorAll("*").forEach((node) => {
            if (node?.style) {
              node.style.animation = "none";
              node.style.transition = "none";
            }
          });
        },
      });

      const pdf = new jsPDF("p", "mm", "a4");
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 12;
      const printableWidth = pageWidth - margin * 2;
      const printableHeight = pageHeight - margin * 2;

      const imgWidth = printableWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let remainingHeight = imgHeight;
      let yOffset = 0;
      let pageNum = 1;

      while (remainingHeight > 0) {
        if (pageNum > 1) pdf.addPage();

        const pageCanvas = document.createElement("canvas");
        pageCanvas.width = canvas.width;
        pageCanvas.height = Math.min(
          canvas.height - yOffset,
          (printableHeight * canvas.width) / imgWidth,
        );
        const ctx = pageCanvas.getContext("2d");
        ctx.drawImage(
          canvas,
          0,
          yOffset,
          canvas.width,
          pageCanvas.height,
          0,
          0,
          canvas.width,
          pageCanvas.height,
        );

        const pageImageData = pageCanvas.toDataURL("image/png");
        pdf.addImage(
          pageImageData,
          "PNG",
          margin,
          margin,
          imgWidth,
          (pageCanvas.height * imgWidth) / canvas.width,
          undefined,
          "FAST",
        );

        pdf.setFontSize(9);
        pdf.text(`Page ${pageNum}`, pageWidth / 2, pageHeight - 6, {
          align: "center",
        });

        remainingHeight -= printableHeight;
        yOffset += pageCanvas.height;
        pageNum++;
      }

      pdf.save(
        `${sanitizeFileName(invoiceData.billData.invoiceNumber || "Invoice")}.pdf`,
      );
      toast.success("Invoice PDF downloaded successfully");

      if (autoCloseOnDownload) {
        closeDialog();
      }

      return true;
    } catch (error) {
      console.error("Error generating invoice PDF:", error);
      toast.error("Failed to generate invoice PDF");
      return false;
    } finally {
      setIsDownloading(false);
      setIsPdfExportMode(false);
    }
  }, [
    autoCloseOnDownload,
    closeDialog,
    invoiceData.billData.invoiceNumber,
    isDownloading,
  ]);

  // ----- Robust auto‑download that waits for the content to be ready -----
  useEffect(() => {
    if (!autoDownload || autoDownloadStartedRef.current) return;

    let isMounted = true;
    const attemptDownload = async () => {
      // Wait for the ref to be available
      while (isMounted && !billContentRef.current) {
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
      if (!isMounted) return;

      // Short additional delay to ensure rendering is complete
      await new Promise((resolve) => setTimeout(resolve, 200));
      if (!isMounted) return;

      autoDownloadStartedRef.current = true;
      handleDownloadPDF();
    };

    attemptDownload();

    return () => {
      isMounted = false;
    };
  }, [autoDownload, handleDownloadPDF]);

  // ----- Column definitions (consistent across layouts) -----
  const summaryColumns = useMemo(
    () => [
      { field: "type", headerName: "Description", width: "22%" },
      { field: "dueDate", headerName: "Due Date", width: "14%" },
      { field: "quantity", headerName: "Qty", width: "10%", align: "right" },
      {
        field: "rate",
        headerName: `Rate (${currencySymbol})`,
        width: "14%",
        align: "right",
      },
      {
        field: "nonBillableAmount",
        headerName: `Non‑billable`,
        width: "16%",
        align: "right",
      },
      {
        field: "billableAmount",
        headerName: `Billable`,
        width: "16%",
        align: "right",
      },
    ],
    [currencySymbol],
  );

  const accountColumns = useMemo(
    () => [
      { field: "invoiceNumber", headerName: "Invoice #", width: "20%" },
      { field: "dueDate", headerName: "Due Date", width: "16%" },
      {
        field: "amountDue",
        headerName: "Amount Due",
        width: "18%",
        align: "right",
      },
      {
        field: "paymentReceived",
        headerName: "Payment Received",
        width: "20%",
        align: "right",
      },
      {
        field: "balanceDue",
        headerName: "Balance Due",
        width: "18%",
        align: "right",
      },
    ],
    [],
  );

  const accountRows = useMemo(
    () => [
      {
        invoiceNumber: invoiceData.billData.invoiceNumber,
        dueDate: invoiceData.billData.dueDate,
        amountDue: invoiceData.billData.totalAmount,
        paymentReceived: invoiceData.billData.paymentReceived,
        balanceDue: invoiceData.billData.balanceDue,
      },
    ],
    [invoiceData],
  );

  // ----- Typography helpers -----
  const sectionTitleStyles = {
    fontSize: compactPdfMode ? "0.9rem" : "1rem",
    fontWeight: 700,
    color: theme.palette.text.primary,
    mb: compactPdfMode ? 1 : 1.5,
    letterSpacing: "-0.01em",
  };

  const tableHeaderCellStyles = {
    padding: compactPdfMode ? "0.6rem 0.5rem" : "0.8rem 0.75rem",
    fontSize: compactPdfMode ? "0.68rem" : "0.75rem",
    fontWeight: 700,
    backgroundColor: theme.palette.grey[100],
    borderBottom: `1px solid ${theme.palette.divider}`,
    lineHeight: 1.4,
    whiteSpace: "normal",
  };

  const tableBodyCellStyles = {
    padding: compactPdfMode ? "0.55rem 0.5rem" : "0.7rem 0.75rem",
    fontSize: compactPdfMode ? "0.66rem" : "0.73rem",
    lineHeight: 1.45,
    borderBottom: `1px solid ${theme.palette.divider}`,
    whiteSpace: "normal",
    wordBreak: "break-word",
    verticalAlign: "top",
  };

  // ----- Table rendering functions -----
  const renderSummaryTable = (hideHeader = false) => (
    <TableContainer sx={{ pageBreakInside: "avoid", overflow: "hidden" }}>
      <Table sx={{ tableLayout: "fixed", width: "100%" }}>
        {!hideHeader && (
          <TableHead>
            <TableRow>
              {summaryColumns.map((column) => (
                <TableCell
                  key={column.field}
                  align={column.align || "left"}
                  sx={{ ...tableHeaderCellStyles, width: column.width }}
                >
                  {column.headerName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {lineItems.length > 0 ? (
            lineItems.map((row) => (
              <TableRow key={row.id}>
                {summaryColumns.map((column) => {
                  let value = row[column.field];

                  if (column.field === "quantity") {
                    value = formatQuantityValue(value);
                  }
                  if (
                    column.field === "rate" ||
                    column.field === "nonBillableAmount" ||
                    column.field === "billableAmount"
                  ) {
                    value = formatCurrencyValue(value);
                  }

                  return (
                    <TableCell
                      key={column.field}
                      align={column.align || "left"}
                      sx={{ ...tableBodyCellStyles, width: column.width }}
                    >
                      {value || "-"}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={summaryColumns.length}
                align="center"
                sx={tableBodyCellStyles}
              >
                No invoice items available.
              </TableCell>
            </TableRow>
          )}

          {lineItems.length > 0 && (
            <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
              <TableCell
                colSpan={4}
                align="right"
                sx={{ ...tableBodyCellStyles, fontWeight: 700 }}
              >
                Totals
              </TableCell>
              <TableCell
                align="right"
                sx={{ ...tableBodyCellStyles, fontWeight: 700 }}
              >
                {formatCurrencyValue(totals.nonBillableTotal)}
              </TableCell>
              <TableCell
                align="right"
                sx={{ ...tableBodyCellStyles, fontWeight: 700 }}
              >
                {formatCurrencyValue(totals.billableTotal)}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderAccountTable = (hideHeader = false) => (
    <TableContainer sx={{ pageBreakInside: "avoid", overflow: "hidden" }}>
      <Table sx={{ tableLayout: "fixed", width: "100%" }}>
        {!hideHeader && (
          <TableHead>
            <TableRow>
              {accountColumns.map((column) => (
                <TableCell
                  key={column.field}
                  align={column.align || "left"}
                  sx={{ ...tableHeaderCellStyles, width: column.width }}
                >
                  {column.headerName}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
        )}
        <TableBody>
          {accountRows.map((row) => (
            <TableRow key={row.invoiceNumber}>
              {accountColumns.map((column) => {
                const isMoneyColumn = [
                  "amountDue",
                  "paymentReceived",
                  "balanceDue",
                ].includes(column.field);

                return (
                  <TableCell
                    key={column.field}
                    align={column.align || "left"}
                    sx={{ ...tableBodyCellStyles, width: column.width }}
                  >
                    {isMoneyColumn
                      ? formatCurrencyValue(row[column.field])
                      : row[column.field] || "-"}
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
          <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
            <TableCell
              colSpan={accountColumns.length - 1}
              align="right"
              sx={{ ...tableBodyCellStyles, fontWeight: 700 }}
            >
              Outstanding Balance
            </TableCell>
            <TableCell
              align="right"
              sx={{ ...tableBodyCellStyles, fontWeight: 700 }}
            >
              {formatCurrencyValue(invoiceData.billData.balanceDue)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );

  // ----- Firm & Client blocks -----
  const renderFirmBlock = ({
    align = "left",
    withInvoiceLabel = false,
    nameColor = theme.palette.text.primary,
    textColor = theme.palette.text.secondary,
  } = {}) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: align === "center" ? "column" : "row",
        alignItems: align === "center" ? "center" : "flex-start",
        justifyContent: align === "center" ? "center" : "space-between",
        gap: 1.5,
        textAlign: align,
      }}
    >
      {invoiceData.firmData.logo && (
        <Box
          component="img"
          src={invoiceData.firmData.logo}
          alt={`${invoiceData.firmData.name} logo`}
          crossOrigin="anonymous"
          sx={{
            width: align === "center" ? "5rem" : "4rem",
            height: align === "center" ? "5rem" : "4rem",
            borderRadius: "1rem",
            objectFit: "contain",
            backgroundColor: "#fff",
            border: `1px solid ${theme.palette.divider}`,
            p: 0.75,
            flexShrink: 0,
          }}
        />
      )}

      <Box>
        {withInvoiceLabel && (
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: textColor,
              mb: 0.5,
            }}
          >
            INVOICE
          </Typography>
        )}
        <Typography
          sx={{
            fontSize: compactPdfMode
              ? align === "center"
                ? "1.1rem"
                : "1rem"
              : align === "center"
                ? "1.35rem"
                : "1.2rem",
            fontWeight: 700,
            color: nameColor,
            lineHeight: 1.2,
          }}
        >
          {invoiceData.firmData.name}
        </Typography>
        <Typography
          sx={{
            fontSize: compactPdfMode ? "0.7rem" : "0.8rem",
            color: textColor,
            mt: 0.5,
          }}
        >
          {[invoiceData.firmData.address, invoiceData.firmData.city]
            .filter(Boolean)
            .join(", ")}
        </Typography>
        <Typography
          sx={{
            fontSize: compactPdfMode ? "0.7rem" : "0.8rem",
            color: textColor,
          }}
        >
          {[invoiceData.firmData.state, invoiceData.firmData.zip]
            .filter(Boolean)
            .join(" ")}
        </Typography>
      </Box>
    </Box>
  );

  const renderClientBlock = ({
    showAvatar = true,
    title = "Bill To",
    compact = false,
  } = {}) => (
    <Box>
      <Typography
        sx={{
          fontSize: compactPdfMode ? "0.68rem" : compact ? "0.7rem" : "0.75rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          color: theme.palette.text.secondary,
          mb: 1,
        }}
      >
        {title}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1 }}>
        {showAvatar && !compactPdfMode && (
          <Avatar
            data-pdf-hide="true"
            src={invoiceData.clientData.avatar}
            imgProps={{ crossOrigin: "anonymous" }}
            sx={{
              width: compact ? 38 : 44,
              height: compact ? 38 : 44,
              bgcolor: theme.palette.primary.main,
              fontWeight: 700,
            }}
          >
            {getInitials(invoiceData.clientData.name)}
          </Avatar>
        )}
        <Box>
          <Typography
            sx={{
              fontSize: compactPdfMode
                ? "0.88rem"
                : compact
                  ? "0.96rem"
                  : "1rem",
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            {invoiceData.clientData.name}
          </Typography>
          <Typography
            sx={{
              fontSize: compactPdfMode ? "0.68rem" : "0.75rem",
              color: "text.secondary",
            }}
          >
            {invoiceData.clientData.matter}
          </Typography>
        </Box>
      </Box>

      <Typography
        sx={{
          fontSize: compactPdfMode ? "0.7rem" : "0.8rem",
          color: "text.secondary",
        }}
      >
        {[invoiceData.clientData.address, invoiceData.clientData.city]
          .filter(Boolean)
          .join(", ")}
      </Typography>
      <Typography
        sx={{
          fontSize: compactPdfMode ? "0.7rem" : "0.8rem",
          color: "text.secondary",
        }}
      >
        {[invoiceData.clientData.state, invoiceData.clientData.zip]
          .filter(Boolean)
          .join(" ")}
      </Typography>
    </Box>
  );

  const renderInvoiceMeta = ({
    align = "left",
    withChip = true,
    inverse = false,
  } = {}) => {
    const valueColor = inverse ? "#fff" : theme.palette.text.primary;
    const labelColor = inverse
      ? "rgba(255,255,255,0.72)"
      : theme.palette.text.secondary;

    return (
      <Box sx={{ textAlign: align }}>
        {[
          ["Invoice No", invoiceData.billData.invoiceNumber],
          ["Issue Date", invoiceData.billData.issueDate],
          ["Due Date", invoiceData.billData.dueDate],
        ].map(([label, value]) => (
          <Box key={label} sx={{ mb: 1 }}>
            <Typography
              sx={{
                fontSize: compactPdfMode ? "0.68rem" : "0.75rem",
                color: labelColor,
              }}
            >
              {label}
            </Typography>
            <Typography
              sx={{
                fontSize: compactPdfMode ? "0.82rem" : "0.9rem",
                fontWeight: 700,
                color: valueColor,
              }}
            >
              {value}
            </Typography>
          </Box>
        ))}

        {withChip && !compactPdfMode && (
          <Chip
            data-pdf-hide="true"
            label={invoiceData.billData.status}
            color={statusTone}
            size="small"
            sx={{
              mt: 0.5,
              textTransform: "capitalize",
              fontWeight: 700,
            }}
          />
        )}
      </Box>
    );
  };

  // ----- Layouts (each refined for a professional law firm) -----
  const renderClassicLayout = () => (
    <>
      <Box sx={{ mb: 3.5 }}>{renderFirmBlock({ align: "center" })}</Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
          mb: 3,
        }}
      >
        <Box
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "1rem",
            p: 2,
          }}
        >
          {renderClientBlock({ showAvatar: true, compact: true })}
        </Box>
        <Box
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "1rem",
            p: 2,
          }}
        >
          {renderInvoiceMeta({ align: "right" })}
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Typography sx={sectionTitleStyles}>Summary of Charges</Typography>
        {renderSummaryTable()}
      </Box>

      <Box>
        <Typography sx={sectionTitleStyles}>Statement of Account</Typography>
        {renderAccountTable()}
      </Box>

      <Typography
        sx={{
          mt: 3,
          fontSize: "0.75rem",
          color: "text.secondary",
          fontStyle: "italic",
        }}
      >
        Please remit payment within 30 days of invoice date.
      </Typography>
    </>
  );

  const renderModernLayout = () => (
    <>
      <Box
        sx={{
          borderRadius: "1.25rem",
          p: { xs: 2, sm: 2.5 },
          mb: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
          color: "#fff",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.25fr 0.75fr" },
            gap: 2.5,
            alignItems: "start",
          }}
        >
          {renderFirmBlock({
            nameColor: "#fff",
            textColor: "rgba(255,255,255,0.8)",
            withInvoiceLabel: true,
          })}
          {renderInvoiceMeta({ align: "right", inverse: true })}
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
          mb: 3,
        }}
      >
        <Card
          variant="outlined"
          sx={{
            p: compactPdfMode ? 1.4 : 2,
            borderRadius: compactPdfMode ? "0.8rem" : "1rem",
            boxShadow: "none",
          }}
        >
          {renderClientBlock({ showAvatar: true })}
        </Card>
        <Card
          variant="outlined"
          sx={{
            p: compactPdfMode ? 1.4 : 2,
            borderRadius: compactPdfMode ? "0.8rem" : "1rem",
            boxShadow: "none",
          }}
        >
          <Typography sx={sectionTitleStyles}>Balance Overview</Typography>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
              Total Amount
            </Typography>
            <Typography sx={{ fontWeight: 700 }}>
              {formatCurrencyValue(invoiceData.billData.totalAmount)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
              Payment Received
            </Typography>
            <Typography sx={{ fontWeight: 700 }}>
              {formatCurrencyValue(invoiceData.billData.paymentReceived)}
            </Typography>
          </Box>
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600 }}>
              Balance Due
            </Typography>
            <Typography
              sx={{
                fontSize: "1rem",
                fontWeight: 700,
                color:
                  invoiceData.billData.balanceDue > 0
                    ? theme.palette.error.main
                    : theme.palette.success.main,
              }}
            >
              {formatCurrencyValue(invoiceData.billData.balanceDue)}
            </Typography>
          </Box>
        </Card>
      </Box>

      <Card variant="outlined" sx={{ borderRadius: "1rem", mb: 2.5 }}>
        <Box sx={{ p: 2.25, pb: 0 }}>
          <Typography sx={sectionTitleStyles}>Summary of Charges</Typography>
        </Box>
        {renderSummaryTable()}
      </Card>

      <Card variant="outlined" sx={{ borderRadius: "1rem" }}>
        <Box sx={{ p: 2.25, pb: 0 }}>
          <Typography sx={sectionTitleStyles}>Statement of Account</Typography>
        </Box>
        {renderAccountTable()}
      </Card>
    </>
  );

  const renderProfessionalLayout = () => (
    <>
      <Box
        sx={{
          borderRadius: "1.5rem",
          p: { xs: 2.25, sm: 3 },
          mb: 3.25,
          background: `linear-gradient(140deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: "#fff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            right: "-1.5rem",
            top: "-1rem",
            width: "7rem",
            height: "7rem",
            borderRadius: "50%",
            backgroundColor: "rgba(255,255,255,0.08)",
          }}
        />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.4fr 0.6fr" },
            gap: 2.5,
            position: "relative",
          }}
        >
          {renderFirmBlock({
            nameColor: "#fff",
            textColor: "rgba(255,255,255,0.82)",
            withInvoiceLabel: true,
          })}
          <Box sx={{ display: "flex", justifyContent: { md: "flex-end" } }}>
            {renderInvoiceMeta({ align: "right", inverse: true })}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1.2fr 0.8fr" },
          gap: 2.5,
          mb: 3,
        }}
      >
        <Card
          sx={{
            p: compactPdfMode ? 1.6 : 2.5,
            borderRadius: compactPdfMode ? "0.9rem" : "1.25rem",
            borderLeft: `4px solid ${theme.palette.primary.main}`,
            boxShadow: "0 0.5rem 1.2rem rgba(0,0,0,0.05)",
          }}
        >
          {renderClientBlock({ showAvatar: true })}
        </Card>

        <Card
          sx={{
            p: compactPdfMode ? 1.6 : 2.5,
            borderRadius: compactPdfMode ? "0.9rem" : "1.25rem",
            boxShadow: "0 0.5rem 1.2rem rgba(0,0,0,0.05)",
          }}
        >
          <Typography sx={sectionTitleStyles}>Invoice Total</Typography>
          <Typography
            sx={{ fontSize: "0.75rem", color: "text.secondary", mb: 0.5 }}
          >
            Current balance due
          </Typography>
          <Typography
            sx={{
              fontSize: compactPdfMode ? "1.2rem" : "1.75rem",
              fontWeight: 800,
              color: theme.palette.primary.main,
              mt: 0.5,
              mb: 2,
            }}
          >
            {formatCurrencyValue(invoiceData.billData.balanceDue)}
          </Typography>
          <Divider sx={{ my: 1.5 }} />
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
              Total billed
            </Typography>
            <Typography sx={{ fontWeight: 700 }}>
              {formatCurrencyValue(invoiceData.billData.totalAmount)}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
              Payments received
            </Typography>
            <Typography sx={{ fontWeight: 700 }}>
              {formatCurrencyValue(invoiceData.billData.paymentReceived)}
            </Typography>
          </Box>
        </Card>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
          <Box
            sx={{
              width: "0.35rem",
              height: "1.3rem",
              borderRadius: "999px",
              backgroundColor: theme.palette.primary.main,
            }}
          />
          <Typography sx={sectionTitleStyles}>Summary of Charges</Typography>
        </Box>
        <Card
          sx={{
            borderRadius: "1.25rem",
            overflow: "hidden",
            boxShadow: "0 0.5rem 1.2rem rgba(0,0,0,0.05)",
          }}
        >
          {renderSummaryTable()}
        </Card>
      </Box>

      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
          <Box
            sx={{
              width: "0.35rem",
              height: "1.3rem",
              borderRadius: "999px",
              backgroundColor: theme.palette.primary.main,
            }}
          />
          <Typography sx={sectionTitleStyles}>Statement of Account</Typography>
        </Box>
        <Card
          sx={{
            borderRadius: "1.25rem",
            overflow: "hidden",
            boxShadow: "0 0.5rem 1.2rem rgba(0,0,0,0.05)",
          }}
        >
          {renderAccountTable()}
        </Card>
      </Box>
    </>
  );

  const renderMinimalLayout = () => (
    <>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr auto" },
          gap: 2,
          alignItems: "center",
          mb: 3,
        }}
      >
        {renderFirmBlock({ align: "left", withInvoiceLabel: true })}
        <Box sx={{ textAlign: { xs: "left", md: "right" } }}>
          <Typography
            sx={{ fontSize: "0.7rem", color: "text.secondary", mb: 0.5 }}
          >
            Status
          </Typography>
          {!compactPdfMode && (
            <Chip
              data-pdf-hide="true"
              label={invoiceData.billData.status}
              color={statusTone}
              size="small"
              sx={{ textTransform: "capitalize", fontWeight: 700 }}
            />
          )}
        </Box>
      </Box>

      <Divider sx={{ mb: 2.5 }} />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 2,
          mb: 3,
        }}
      >
        {renderClientBlock({ showAvatar: true, compact: true })}
        {renderInvoiceMeta({ align: "right", withChip: false })}
      </Box>

      <Typography sx={sectionTitleStyles}>Summary of Charges</Typography>
      {renderSummaryTable(true)}

      <Divider sx={{ my: 2.5 }} />

      <Typography sx={sectionTitleStyles}>Account Summary</Typography>
      {renderAccountTable(true)}
    </>
  );

  const renderLayout = () => {
    switch (resolvedLayout) {
      case "modern":
        return renderModernLayout();
      case "professional":
        return renderProfessionalLayout();
      case "minimal":
        return renderMinimalLayout();
      case "classic":
      default:
        return renderClassicLayout();
    }
  };

  const showPreviewActions = !previewMode && !autoCloseOnDownload;

  return (
    <Box
      sx={{
        bgcolor: previewMode ? "transparent" : theme.palette.background.default,
        display: "flex",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: compactPdfMode ? A4_SHEET_WIDTH : "49.625rem",
        }}
      >
        <Card
          ref={billContentRef}
          data-bill-pdf="true"
          sx={{
            p: compactPdfMode
              ? { xs: "1.2rem", sm: "1.5rem" }
              : previewMode
                ? "2rem"
                : "2.4rem",
            width: "100%",
            maxWidth: compactPdfMode ? A4_SHEET_WIDTH : "49.625rem",
            minHeight: compactPdfMode ? "297mm" : "auto",
            margin: "0 auto",
            borderRadius: compactPdfMode ? "0.9rem" : "1.5rem",
            overflow: "hidden",
            boxShadow: previewMode || compactPdfMode ? "none" : undefined,
            backgroundColor: "#ffffff",
            lineHeight: compactPdfMode ? 1.35 : 1.45,
            textRendering: "geometricPrecision",
            WebkitFontSmoothing: "antialiased",
          }}
        >
          {renderLayout()}
        </Card>
      </Box>
      {showPreviewActions && (
        <Box
          sx={{
            height: "fit-content",
            borderTop: "1px solid #ddd",
            position: "sticky",
            bottom: 0,
            backdropFilter: "blur(10px)",
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            p: "0.5rem",
            zIndex: 1000,
            gap: "0.5rem",
            bgcolor: "rgba(255, 255, 255, 0.8)",
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<CloseRounded />}
            onClick={() => closeDialog()}
            sx={{ textTransform: "none" }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<DownloadRounded />}
            onClick={handleDownloadPDF}
            disabled={isDownloading}
            sx={{ textTransform: "none" }}
          >
            {isDownloading ? "Preparing PDF…" : "Download PDF"}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default BillPreview;
